import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useAppSelector } from "@/app/store";
import { selectCognitoConfig } from "@/features/app-config/model/tenantConfigurationSlice";
import {
  AuthContext,
  type AuthContextValue,
} from "@/shared/auth/authContext";
import {
  clearTokenRefreshInFlight,
  ensureValidBearerToken,
  registerAuthTokenSyncHandler,
} from "@/shared/auth/ensureValidBearerToken";
import { jwtMatchesUserPool } from "@/shared/auth/jwtClaims";
import { resolveAuthRealm } from "@/shared/auth/authRealm";
import {
  clearStoredTokens,
  isJwtExpiringSoon,
  readStoredTokens,
  storeTokens,
  type CognitoTokens,
} from "@/shared/auth/cognito";
import { setCognitoRuntimeConfig } from "@/shared/auth/cognitoRuntimeConfig";

type StoredAuth = {
  idToken: string;
  refreshToken: string;
};

type AuthProviderProps = {
  children: ReactNode;
};

function hydrateStoredAuth(realm: ReturnType<typeof resolveAuthRealm>): StoredAuth | null {
  const stored = readStoredTokens(realm);
  if (!stored) {
    return null;
  }
  return stored;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const realm = resolveAuthRealm();
  const cognitoConfig = useAppSelector(selectCognitoConfig);

  const [auth, setAuth] = useState<StoredAuth | null>(() => hydrateStoredAuth(realm));

  const setTokens = useCallback(
    (tokens: CognitoTokens) => {
      storeTokens(realm, tokens);
      setAuth({ idToken: tokens.id_token, refreshToken: tokens.refresh_token });
    },
    [realm],
  );

  const logout = useCallback(() => {
    clearTokenRefreshInFlight();
    clearStoredTokens(realm);
    setAuth(null);
  }, [realm]);

  useEffect(() => {
    if (!cognitoConfig) {
      setCognitoRuntimeConfig(null);
      return;
    }
    setCognitoRuntimeConfig({
      userPoolId: cognitoConfig.userPoolId,
      clientId: cognitoConfig.clientId,
      cognitoDomain: cognitoConfig.cognitoDomain,
    });
  }, [cognitoConfig]);

  useEffect(() => {
    registerAuthTokenSyncHandler((tokens) => {
      storeTokens(realm, tokens);
      setAuth((prev) => ({
        idToken: tokens.id_token,
        refreshToken: tokens.refresh_token ?? prev?.refreshToken ?? "",
      }));
    });
    return () => registerAuthTokenSyncHandler(null);
  }, [realm]);

  useEffect(() => {
    if (!auth?.refreshToken || !cognitoConfig) {
      return;
    }
    if (!auth.idToken || isJwtExpiringSoon(auth.idToken)) {
      void ensureValidBearerToken(realm);
    }
  }, [auth?.refreshToken, auth?.idToken, cognitoConfig, realm]);

  useEffect(() => {
    if (!auth?.idToken || !cognitoConfig?.userPoolId) {
      return;
    }
    if (!jwtMatchesUserPool(auth.idToken, cognitoConfig.userPoolId)) {
      clearStoredTokens(realm);
      setAuth(null);
    }
  }, [auth?.idToken, cognitoConfig, realm]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: auth !== null && !!auth.refreshToken,
      idToken: auth?.idToken ?? null,
      setTokens,
      logout,
    }),
    [auth, setTokens, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
